import { useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { useInternalI18n } from '../../i18n/context';
import { useDropdownStatus } from '../../internal/components/dropdown-status';
/**
 * Ids of a group's status element and its recovery button. `menuId` is only set in filtering
 * mode, where the filter input points at these elements through `aria-activedescendant`.
 */
export function getGroupStatusIds(menuId, groupId) {
    return {
        statusId: `status-${menuId}-${groupId}`,
        recoveryButtonId: `recovery-${menuId}-${groupId}`,
    };
}
/**
 * Derives the async loading status UI of an expandable group from `getExpandableItemsAsyncLoadingState`.
 * Shared by the desktop (fly-out) and mobile (inline) expandable category elements so both render the
 * same loading, error, empty, and finished states.
 */
export function useExpandableGroupStatus({ item, asyncLoadingProps, getExpandableItemsAsyncLoadingState, onGroupRecoveryClick, filteringEnabled, menuId, triggerRef, }) {
    var _a, _b, _c, _d, _e, _f;
    const groupId = item.id;
    const uniqueFooterId = useUniqueId('awsui-button-dropdown__group-footer');
    // In filtering mode the root references the status and the recovery button through
    // `aria-activedescendant`, so their ids must be derivable from the menu and group ids.
    const statusIds = menuId && groupId ? getGroupStatusIds(menuId, groupId) : undefined;
    const footerId = (_a = statusIds === null || statusIds === void 0 ? void 0 : statusIds.statusId) !== null && _a !== void 0 ? _a : uniqueFooterId;
    const hasGroupItems = !!item.items && item.items.length > 0;
    // A null or undefined status is treated as `finished`, as documented.
    const statusType = (_b = (groupId ? getExpandableItemsAsyncLoadingState === null || getExpandableItemsAsyncLoadingState === void 0 ? void 0 : getExpandableItemsAsyncLoadingState({ item }) : undefined)) !== null && _b !== void 0 ? _b : 'finished';
    const i18n = useInternalI18n('button-dropdown');
    const recoveryText = i18n('recoveryText', asyncLoadingProps === null || asyncLoadingProps === void 0 ? void 0 : asyncLoadingProps.recoveryText);
    const errorIconAriaLabel = i18n('errorIconAriaLabel', asyncLoadingProps === null || asyncLoadingProps === void 0 ? void 0 : asyncLoadingProps.errorIconAriaLabel);
    const status = useDropdownStatus({
        statusType,
        empty: (_c = asyncLoadingProps === null || asyncLoadingProps === void 0 ? void 0 : asyncLoadingProps.empty) === null || _c === void 0 ? void 0 : _c.call(asyncLoadingProps, groupId),
        loadingText: (_d = asyncLoadingProps === null || asyncLoadingProps === void 0 ? void 0 : asyncLoadingProps.loadingText) === null || _d === void 0 ? void 0 : _d.call(asyncLoadingProps, groupId),
        finishedText: (_e = asyncLoadingProps === null || asyncLoadingProps === void 0 ? void 0 : asyncLoadingProps.finishedText) === null || _e === void 0 ? void 0 : _e.call(asyncLoadingProps, groupId),
        errorText: (_f = asyncLoadingProps === null || asyncLoadingProps === void 0 ? void 0 : asyncLoadingProps.errorText) === null || _f === void 0 ? void 0 : _f.call(asyncLoadingProps, groupId),
        recoveryText,
        errorIconAriaLabel,
        isEmpty: statusType === 'finished' && !hasGroupItems,
        isNoMatch: false,
        hasRecoveryCallback: !!onGroupRecoveryClick,
        recoveryButtonId: statusIds === null || statusIds === void 0 ? void 0 : statusIds.recoveryButtonId,
        onRecoveryClick: () => {
            var _a;
            if (groupId) {
                onGroupRecoveryClick === null || onGroupRecoveryClick === void 0 ? void 0 : onGroupRecoveryClick(groupId);
            }
            // The recovery button disappears once loading starts. In menu mode keep focus on the group
            // header so the dropdown stays open; in filtering mode the root moves focus back to the filter.
            if (!filteringEnabled) {
                (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        },
    });
    return { status, footerId, hasGroupItems };
}
//# sourceMappingURL=use-expandable-group-status.js.map